import React, { useState, useRef, useEffect } from 'react';
import { post } from '../../../services/smartApiService';
import { AI_TRADER_URLS } from '../../../api/AiTraderUrls';

interface Message {
  role: 'user' | 'assistant';
  text: string;
}

interface Props {
  symbol: string;
}

const QUICK_QUESTIONS = [
  'Which strike and expiry is best for today?',
  'What is the max pain level and how does it affect price?',
  'Should I wait for a pullback before entering?',
  'What is the risk:reward for current recommendation?',
  'Are there any macro risks today I should be aware of?',
];

export default function AITraderChat({ symbol }: Props) {
  const [messages, setMessages] = useState<Message[]>([{
    role: 'assistant',
    text: `Hi! I'm your AI options trading assistant with full live context on ${symbol}. Ask me anything about today's setup, strike selection, or risk management.`,
  }]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Reset greeting when symbol changes
  useEffect(() => {
    setMessages([{
      role: 'assistant',
      text: `Switched to ${symbol}. I have full live context loaded. Ask me anything.`,
    }]);
  }, [symbol]);

  const send = (question?: string) => {
    const q = (question || input).trim();
    if (!q || loading) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: q }]);
    setLoading(true);

    post(AI_TRADER_URLS.CHAT, { question: q, symbol }, { showLoading: false }).subscribe({
      next: (res: any) => {
        const answer = res?.data?.answer || res?.answer || 'No response received.';
        setMessages(prev => [...prev, { role: 'assistant', text: answer }]);
        setLoading(false);
      },
      error: (err: any) => {
        setMessages(prev => [...prev, { role: 'assistant', text: 'Error connecting to Grok. Check API key in .env.' }]);
        setLoading(false);
      },
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 380 }}>
      {/* Context badge */}
      <div style={{
        padding: '5px 10px', borderBottom: '1px solid #1a1a1a',
        display: 'flex', alignItems: 'center', gap: 6,
      }}>
        <span style={{
          background: '#0d3320', color: '#26a65b', fontSize: '0.6rem',
          padding: '2px 8px', borderRadius: 10,
        }}>
          ● Context: {symbol} signal · OI levels · OI delta · VIX · expiry · sentiment · global markets pre-loaded
        </span>
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '8px 10px' }}>
        {messages.map((msg, i) => (
          <div key={i} style={{
            marginBottom: 8,
            display: 'flex',
            justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
          }}>
            <div style={{
              maxWidth: '85%',
              background: msg.role === 'user' ? '#1a3a5c' : '#1a1a2e',
              border: `1px solid ${msg.role === 'user' ? '#2d5a8c' : '#2a2a3a'}`,
              borderRadius: msg.role === 'user' ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
              padding: '7px 12px',
              color: '#ddd',
              fontSize: '0.72rem',
              lineHeight: 1.5,
              whiteSpace: 'pre-wrap',
            }}>
              {msg.text}
            </div>
          </div>
        ))}
        {loading && (
          <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 8 }}>
            <div style={{
              background: '#1a1a2e', border: '1px solid #2a2a3a',
              borderRadius: '12px 12px 12px 2px', padding: '7px 16px',
              color: '#555', fontSize: '0.72rem',
            }}>
              Grok is thinking...
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Quick questions */}
      <div style={{ padding: '4px 8px', borderTop: '1px solid #1a1a1a', display: 'flex', gap: 4, overflowX: 'auto' }}>
        {QUICK_QUESTIONS.map((q, i) => (
          <button key={i} onClick={() => send(q)} disabled={loading} style={{
            flexShrink: 0, background: '#0d1a2a', border: '1px solid #1a3a5c',
            color: '#4fc3f7', fontSize: '0.6rem', padding: '3px 8px', borderRadius: 10,
            cursor: 'pointer', whiteSpace: 'nowrap',
            opacity: loading ? 0.5 : 1,
          }}>
            {q}
          </button>
        ))}
      </div>

      {/* Input */}
      <div style={{ padding: '6px 8px', borderTop: '1px solid #1a1a1a', display: 'flex', gap: 6 }}>
        <input
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && !e.shiftKey && send()}
          placeholder={`Ask about ${symbol} options trading...`}
          disabled={loading}
          style={{
            flex: 1, background: '#111', border: '1px solid #2a2a3a',
            borderRadius: 6, padding: '6px 10px', color: '#ddd',
            fontSize: '0.72rem', outline: 'none',
          }}
        />
        <button onClick={() => send()} disabled={loading || !input.trim()} style={{
          background: '#1a3a5c', border: 'none', color: '#4fc3f7',
          padding: '6px 14px', borderRadius: 6, cursor: 'pointer',
          fontSize: '0.72rem', fontWeight: 700,
          opacity: loading || !input.trim() ? 0.5 : 1,
        }}>
          Send
        </button>
      </div>
    </div>
  );
}
